import React, { useState } from 'react';
import { Shield, Search, Filter, RefreshCw, Download, ExternalLink, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';

const CVEList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sample data - in production, this would come from an API
  const cves = Array.from({ length: 50 }, (_, i) => ({
    id: `CVE-2024-${1000 + i}`,
    title: `Security vulnerability in ${i % 2 === 0 ? 'authentication system' : 'data processing'}`,
    severity: i % 4 === 0 ? 'critical' : i % 4 === 1 ? 'high' : i % 4 === 2 ? 'medium' : 'low',
    cvss: (Math.random() * 10).toFixed(1),
    status: i % 3 === 0 ? 'new' : i % 3 === 1 ? 'analyzing' : 'patched',
    affectedAssets: Math.floor(Math.random() * 10),
    publishedAt: new Date(Date.now() - Math.random() * 10000000000).toISOString(),
    updatedAt: new Date(Date.now() - Math.random() * 1000000000).toISOString()
  }));

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedCVEs = cves.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">CVE List</h1>
            <p className="text-sm text-gray-400">Track and manage Common Vulnerabilities and Exposures</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <input
            type="text"
            placeholder="Search CVEs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      {/* CVEs Table */}
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">CVE ID</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Title</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Severity</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">CVSS</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Affected Assets</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Published</th>
              {/* <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th> */}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {displayedCVEs.map((cve) => (
              <tr key={cve.id} className="group hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <span className="text-sm font-mono text-white">{cve.id}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-white">{cve.title}</span>
                </td>
                <td className="px-6 py-4">
                  <Badge variant={cve.severity as any}>{cve.severity}</Badge>
                </td>
                <td className="px-6 py-4">
                  <div className={`text-sm font-medium ${
                    Number(cve.cvss) >= 9.0 ? 'text-red-500' :
                    Number(cve.cvss) >= 7.0 ? 'text-orange-500' :
                    Number(cve.cvss) >= 4.0 ? 'text-yellow-500' :
                    'text-emerald-500'
                  }`}>
                    {cve.cvss}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Badge variant={cve.status as any}>{cve.status}</Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-1">
                    <AlertTriangle className={`h-4 w-4 ${
                      cve.affectedAssets > 5 ? 'text-red-500' :
                      cve.affectedAssets > 2 ? 'text-yellow-500' :
                      'text-emerald-500'
                    }`} />
                    <span className="text-sm text-gray-300">{cve.affectedAssets}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">
                    {new Date(cve.publishedAt).toLocaleString()}
                  </span>
                </td>
                {/* <td className="px-6 py-4">
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="outline" size="sm">
                      <Shield className="h-4 w-4 mr-2" />
                      Analyze
                    </Button>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View
                    </Button>
                  </div>
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {startIndex + 1} to {Math.min(endIndex, cves.length)} of {cves.length} CVEs
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-500">
              {currentPage}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={endIndex >= cves.length}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVEList;